import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Divider,
  Container,
} from '@mui/material';

import AccountOverview from './AccountOverview';
import Subscription from './Subscription';
import UsersAndTeams from './UsersAndTeams';
import Integrations from './Integrations';
import AssignmentRules from './AssignmentRules';
import ReassignmentRules from './ReassignmentRules';
import WhatsAppSettings from './WhatsAppSettings';
import { useAuth } from '../../contexts/AuthContext';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

const Settings = () => {
  const [value, setValue] = useState(0);
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  // Define all possible tabs with URL slug for each
  const allTabs = [
    { label: "Account Overview", tabSlug: 'account-overview', component: <AccountOverview /> },
    { label: "Subscription", tabSlug: 'subscription', component: <Subscription /> },
    { label: "Users & Teams", tabSlug: 'users-teams', component: <UsersAndTeams /> },
    { label: "Integrations", tabSlug: 'integrations', component: <Integrations /> },
    { label: "WhatsApp Settings", tabSlug: 'whatsapp-settings', component: <WhatsAppSettings /> },
    { label: "Assignment Rules", tabSlug: 'assignment-rules', component: <AssignmentRules /> },
    { label: "Reassignment Rules", tabSlug: 'reassignment-rules', component: <ReassignmentRules /> }
  ];

  const handleChange = (event, newValue) => {
    setValue(newValue);
    const selected = tabs[newValue];
    const next = new URLSearchParams(searchParams);
    // Subscription: keep sub-tabs (usage, spendings, billing) in URL when already set
    if (selected?.tabSlug === 'subscription') {
      const current = next.get('tab');
      if (current !== 'usage' && current !== 'spendings' && current !== 'billing') {
        next.set('tab', 'subscription');
      }
    } else {
      next.set('tab', selected?.tabSlug ?? '');
    }
    setSearchParams(next, { replace: true });
  };

  // Filter tabs based on user role
  const tabs = allTabs.filter((tab, index) => {
    // Account Overview (index 0) - show for AD
    if (index === 0) return user?.role === 'AD';
    // Subscription (index 1) - show for AD; includes Billing & Invoice sub-tab
    if (index === 1) return user?.role === 'AD';
    // Users & Teams (index 2) - show for AD and MG
    if (index === 2) return user?.role === 'AD' || user?.role === 'MG';
    // Integrations (index 3) - show for AD
    if (index === 3) return user?.role === 'AD';
    // WhatsApp Settings (index 4) - show for AD
    if (index === 4) return user?.role === 'AD';
    // Assignment Rules (index 5) - show for AD and MG
    if (index === 5) return user?.role === 'AD' || user?.role === 'MG';
    // Reassignment Rules (index 6) - show for AD and MG
    if (index === 6) return user?.role === 'AD' || user?.role === 'MG';
    return false;
  });

  // When loaded in iframe after payment redirect, tell parent to close modal and show subscription.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const inIframe = window.self !== window.top;
    const paymentSuccess = searchParams.get('payment_success') === '1';
    if (inIframe && paymentSuccess) {
      try {
        window.parent.postMessage({ type: 'PAYMENT_SUCCESS' }, window.location.origin);
      } catch (e) {
        // ignore
      }
    }
  }, [searchParams]);

  // Sync active tab from URL ?tab=<slug>
  useEffect(() => {
    setLoading(false);

    const tabParam = searchParams.get('tab');
    if (!tabParam) return;

    // Subscription panel: ?tab=subscription | usage | spendings | billing
    if (tabParam === 'subscription' || tabParam === 'usage' || tabParam === 'spendings' || tabParam === 'billing') {
      const idx = tabs.findIndex(tab => tab.tabSlug === 'subscription');
      if (idx !== -1) setValue(idx);
      return;
    }

    // Other tabs: match by tabSlug
    const idx = tabs.findIndex(tab => tab.tabSlug === tabParam);
    if (idx !== -1) setValue(idx);
  }, [searchParams, tabs]);

  // Show loading state while auth is being checked
  if (loading) {
    return (
      <Container maxWidth={false} disableGutters>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography>Loading settings...</Typography>
        </Box>
      </Container>
    );
  }

  // Show error state if no user
  if (!user) {
    return (
      <Container maxWidth={false} disableGutters>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error">User not found. Please log in again.</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth={false} 
      disableGutters
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        px: 2, 
        py: 1.5, 
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        <Typography variant="body1" color="text.secondary">
          Manage your account settings and preferences
        </Typography>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        flex: 1,
        overflow: 'auto',
        px: 2,
        pt: 1
      }}>
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="settings tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tab, index) => (
              <Tab key={index} label={tab.label} {...a11yProps(index)} />
            ))}
          </Tabs>
        </Box>
        
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {tabs.map((tab, index) => (
            <TabPanel key={index} value={value} index={index}>
              {tab.component}
            </TabPanel>
          ))}
        </Box>
      </Box>
    </Container>
  );
};

export default Settings;